import {
  Button,
  ChildUser,
  CustomRewardCurrency,
  Family,
  FlexBox,
  JoonUIColor,
  Modal,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import { useEffect } from "react"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"

import usePurchaseRewardModalStore from "./hooks/usePurchaseRewardModalStore"
import PurchaseRewardChildSelectCard from "./PurchaseRewardChildSelectCard"
import RewardsCounter from "../../components/counter/RewardsCounter"
import { useFamilyQuery } from "../../networking/queries"
import { usePurchaseRewardMutation } from "../../networking/rewards/mutations"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { getUserFromFamily } from "../../util/util"

const PurchaseRewardModal = () => {
  const {
    isOpen,
    onClose,
    reward,
    selectedChildId,
    setSelectedChildId,
    quantity,
    setQuantity,
  } = usePurchaseRewardModalStore()

  const { data: family } = useFamilyQuery()
  const { t } = useTranslation()
  const children = reward?.assignments?.map((assignment) =>
    getUserFromFamily(family as Family, assignment.user_id)
  ) as ChildUser[]

  const purchaseRewardMutation = usePurchaseRewardMutation()

  const onPurchaseClick = async () => {
    if (!selectedChildId || !reward) return
    await purchaseRewardMutation.mutateAsync({
      rewardId: reward.id,
      childId: selectedChildId,
      amount: quantity,
    })
    const isOne = quantity === 1
    const purchasedKey =
      reward.currency === CustomRewardCurrency.DOLLAR
        ? isOne
          ? "PRM_PURCHASED_DOLLAR_ONE"
          : "PRM_PURCHASED_DOLLAR_OTHER"
        : reward.currency === CustomRewardCurrency.MINUTE
          ? isOne
            ? "PRM_PURCHASED_MINUTE_ONE"
            : "PRM_PURCHASED_MINUTE_OTHER"
          : isOne
            ? "PRM_PURCHASED_REWARD_ONE"
            : "PRM_PURCHASED_REWARD_OTHER"
    toast.success(t(purchasedKey, { count: quantity }))
    trackAnalyticEvent(ANALYTIC_EVENTS.PURCHASE_CUSTOM_REWARD)
    onClose()
  }

  useEffect(() => {
    if (selectedChildId === null && children?.length && children.length >= 1)
      setSelectedChildId(children[0].id)
  }, [children, selectedChildId, setSelectedChildId])

  if (!family || !reward) return <></>

  const childrenWithEnoughCoins = children.filter(
    (child) => child.coins >= reward.cost
  )
  const maxQuantity =
    Math.floor(
      (childrenWithEnoughCoins.find((child) => child.id === selectedChildId)
        ?.coins ?? 0) / (reward.cost ?? 1)
    ) ?? 0

  return (
    <Modal onClose={onClose} isOpen={isOpen} displayCloseIcon animate>
      <div style={{ width: "min(400px, 95vw", padding: SPACING.space6 }}>
        {childrenWithEnoughCoins.length === 0 ? (
          <FlexBox
            direction="column"
            gap={SPACING.space4}
            style={{ textAlign: "left" }}
          >
            <Typography variant="bodyBold" textAlign="left">
              {t("PRM_NOT_ENOUGH_COINS")}
            </Typography>
            <Typography variant="bodySmall" textAlign="left">
              {t("PRM_ASSIGN_MORE")}
            </Typography>
            <Button text={t("PRM_OK")} onClick={onClose} fullWidth />
          </FlexBox>
        ) : (
          <FlexBox direction="column" align="left" gap={SPACING.space6}>
            <FlexBox direction="column" gap={SPACING.space4}>
              <Typography variant="bodyBold">
                {t("PRM_PURCHASING", {
                  name: `${reward.emoji} ${reward.title}`,
                })}
              </Typography>
              <FlexBox direction="column" gap={SPACING.space1}>
                <Typography
                  variant="bodyXSmallBold"
                  color={JoonUIColor.text.secondary}
                >
                  {t("REWARD_COST")}
                </Typography>
                <FlexBox
                  gap={SPACING.space1}
                  direction="row"
                  wrap={false}
                  align="center"
                >
                  <img
                    style={{ height: SPACING.space3 }}
                    src="/images/icons/coin-icon.png"
                    alt="coin"
                  />
                  <Typography variant="bodySmall">{reward?.cost}</Typography>
                </FlexBox>
              </FlexBox>
            </FlexBox>

            <FlexBox direction="column" gap={SPACING.space2} wrap={false}>
              <Typography variant="bodyBold">{t("PRM_WHO_FOR")}</Typography>
              <Typography variant="bodySmall">
                {t("PRM_ONLY_ENOUGH_COINS")}
              </Typography>
              {childrenWithEnoughCoins.map((child) => (
                <PurchaseRewardChildSelectCard child={child} />
              ))}
            </FlexBox>

            <FlexBox direction="column" gap={SPACING.space4} wrap={false}>
              <Typography variant="bodyBold">
                {reward.currency === CustomRewardCurrency.DOLLAR
                  ? t("PRM_HOW_MANY_DOLLARS")
                  : reward.currency === CustomRewardCurrency.MINUTE
                    ? t("PRM_HOW_MANY_MINUTES")
                    : t("PRM_HOW_MANY")}
              </Typography>
              <RewardsCounter
                maxQuantity={maxQuantity}
                quantity={quantity}
                setQuantity={setQuantity}
              />
              <FlexBox
                direction="row"
                align="center"
                gap={SPACING.space1}
                wrap={false}
                style={{ width: "fit-content" }}
              >
                <Typography variant="bodySmall" style={{ userSelect: "none" }}>
                  {t("PRM_TOTAL_COST")}
                </Typography>
                <img
                  style={{ height: SPACING.space3 }}
                  src="/images/icons/coin-icon.png"
                  alt="coin"
                />
                <Typography variant="bodySmall">
                  {reward.cost * quantity}
                </Typography>
              </FlexBox>
            </FlexBox>

            <Button
              text={t("REWARD_PURCHASE")}
              isLoading={purchaseRewardMutation.isLoading}
              onClick={onPurchaseClick}
              disabled={
                !selectedChildId || quantity < 1 || quantity > maxQuantity
              }
              fullWidth
              style={{ userSelect: "none" }}
            />
          </FlexBox>
        )}
      </div>
    </Modal>
  )
}

export default PurchaseRewardModal
